'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { ImageType } from '@eliadmin/core/src/api/api-types';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Link from '@eliadmin/core/src/components/link';
import Image from 'next/image';
import { LuArrowLeft, LuArrowRight, LuX } from 'react-icons/lu';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export type Category = {
  data?: Category[];
  childs: Category[];
  name: string;
  description: string;
  short_description: string;
  slug: string;
  icon?: string;
  image?: ImageType;
};

type CategoriesPageNewProps = {
  categories: Category[];
};

export const CategoriesPageNew = ({ categories }: CategoriesPageNewProps) => {
  const { t } = useTranslations();
  return (
    <section className={cn('custom-section', 'gap-12 pb-24 pt-12 sm:pt-16 md:pt-20 lg:pt-24')}>
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-6 sm:gap-8 md:gap-10 lg:gap-12',
        )}
      >
        <Heading title={t('text176')} />
        <div className="hidden w-full grid-cols-1 gap-2 sm:grid sm:grid-cols-2 md:gap-4 lg:grid-cols-3">
          {categories.map(category => (
            <CategoryCard key={category.slug} category={category} isDesktop={true} />
          ))}
        </div>
        <div className="grid w-full grid-cols-1 gap-2 sm:hidden">
          {categories.map(category => (
            <CategoryCard key={category.slug} category={category} isDesktop={false} />
          ))}
        </div>
      </div>
    </section>
  );
};

function CategoryCard({ category, isDesktop }: { category: Category; isDesktop: boolean }) {
  if (isDesktop)
    return (
      <Link
        key={category.slug}
        href={ROUTES.CATEGORY({ slug: category?.slug })}
        prefetchOnHover
        className={cn(
          'col-span-1 flex h-[52px] w-full items-center justify-between gap-4 rounded-[20px] border p-4 transition-all hover:bg-border/25 lg:p-6 xl:h-14',
        )}
      >
        <div className="flex items-center justify-start gap-2">
          <Image
            src={category?.icon?.url ?? '/images/placeholder.png'}
            alt={resolveImageAlt(category?.icon, category?.name)}
            width={24}
            height={24}
            className="shrink-0"
          />
          <h4 className="text-left text-lg font-medium tracking-tighter text-foreground lg:text-xl">
            {category?.name}
          </h4>
        </div>
      </Link>
    );

  return (
    <Sheet>
      <SheetTrigger className="col-span-1 flex h-[52px] w-full items-center justify-between rounded-[20px] border p-4 hover:bg-border/25">
        <div className="flex items-center justify-start gap-2">
          <Image
            src={category?.icon?.url ?? '/images/placeholder.png'}
            alt={resolveImageAlt(category?.icon, category?.name)}
            width={24}
            height={24}
            className="shrink-0"
          />
          <Heading tag="h5" title={category.name} />
        </div>
        {category?.childs && category?.childs?.length > 0 && (
          <LuArrowRight size={20} className="text-primary" />
        )}
      </SheetTrigger>
      <SheetContent
        closeIcon={{ wrapper: 'hidden' }}
        className="hide-scroll-bar flex h-screen w-full max-w-[640px] flex-col items-start justify-start overflow-y-scroll p-0"
      >
        <div className="flex w-full items-center justify-end px-4 py-3.5">
          <SheetClose>
            <LuX size={24} className="text-black" />
          </SheetClose>
        </div>
        <div className="flex w-full flex-col items-start justify-start gap-4 p-4">
          <div className="flex items-center justify-start gap-2">
            <SheetClose>
              <LuArrowLeft size={24} className="text-primary" />
            </SheetClose>
            <SheetClose asChild>
              <Link prefetchOnHover href={ROUTES.CATEGORY({ slug: category?.slug })}>
                <Heading title={category?.name} tag="h3" />
              </Link>
            </SheetClose>
          </div>
          <div className="flex w-full flex-col items-start justify-start gap-2">
            {category.childs.map(child => (
              <SheetClose
                asChild
                key={child.slug}
                className="flex h-12 w-full items-center justify-start gap-2 rounded-[20px] border p-4 transition-all hover:bg-border/25"
              >
                <Link prefetchOnHover href={ROUTES.CATEGORY({ slug: child?.slug })}>
                  {!!child?.icon?.url && (
                    <div className="relative max-h-12 min-h-12 min-w-12 max-w-12 rounded-[20px]">
                      <Image
                        src={child?.icon?.url}
                        alt={resolveImageAlt(child?.icon, child?.name)}
                        fill
                        className="object-contain object-center"
                      />
                    </div>
                  )}
                  <Heading tag="h5" title={child?.name} />
                </Link>
              </SheetClose>
            ))}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
